import { useEffect, useState, useCallback, useMemo } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import httpClient from "api/httpClient";
import ConfirmationDialog from "components/Common/ConfirmationDialog";
import { CustomTablePagination } from "components/Common/CustomTablePagination";
import { CustomMenuButton } from "components/Common/CustomMenuButton";
import SchemaDialogForm from "components/Form/SchemaDialogForm";
import CoPaysTableData from "./data/CoPaysTableData";
import { coPayFormSchema, coPayRecord } from "./data/CoPaysSchemas";
import { handleApiError } from "utils/apiError";

function CoPays() {
  const apiBase = process.env.REACT_APP_API_URL;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(coPayRecord);
  const [saving, setSaving] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [policyItemsLoading, setPolicyItemsLoading] = useState(false);
  const [policyTypesLoading, setPolicyTypesLoading] = useState(false);
  const [terminalsLoading, setTerminalsLoading] = useState(false);
  const [policyItems, setPolicyItems] = useState([]);

  // Form dependencies (Select lists)
  const [policyTypes, setPolicyTypes] = useState([]);
  const [terminals, setTerminals] = useState([]);

  const loadPolicyItems = useCallback(
    async (search = "") => {
      try {
        setPolicyItemsLoading(true);
        const url = `${apiBase}/api/policy-items/search-list?page=0&size=20&q=${search}`;
        const res = await httpClient.get(url);
        const cardData = res.data.content || [];
        setPolicyItems(
          cardData.map((policyItem) => ({
            ...policyItem,
            label: `${policyItem.code} (${policyItem.name})`,
          }))
        );
      } catch (err) {
        console.error("Failed to load policy items", err);
      } finally {
        setPolicyItemsLoading(false);
      }
    },
    [apiBase]
  );

  const loadTerminals = useCallback(
    async (search = "") => {
      try {
        setTerminalsLoading(true);
        const url = `${apiBase}/api/terminals/search-list?page=0&size=20&q=${search}`;
        const res = await httpClient.get(url);
        const cardData = res.data.content || [];
        setTerminals(
          cardData.map((terminal) => ({
            ...terminal,
            label: `${terminal.terminalName} (${terminal.code})`,
          }))
        );
      } catch (err) {
        console.error("Failed to load terminals", err);
      } finally {
        setTerminalsLoading(false);
      }
    },
    [apiBase]
  );

  const loadPolicyTypes = useCallback(
    async (search = "") => {
      try {
        setPolicyTypesLoading(true);
        const url = `${apiBase}/api/policy-types/search-list?page=0&size=20&q=${search}`;
        const res = await httpClient.get(url);
        const cardData = res.data.content || [];
        setPolicyTypes(
          cardData.map((policyType) => ({
            ...policyType,
            label: `${policyType.code} (${policyType.name})`,
          }))
        );
      } catch (err) {
        console.error("Failed to load policy types", err);
      } finally {
        setPolicyTypesLoading(false);
      }
    },
    [apiBase]
  );

  useEffect(() => {
    loadPolicyItems();
    loadTerminals();
    loadPolicyTypes();
  }, [loadTerminals, loadPolicyItems, loadPolicyTypes]);

  const [sb, setSb] = useState({ open: false, title: "", type: "error", message: "" });
  const showSnackBar = (title, type, message) => setSb({ open: true, title, type, message });

  const loadCoPays = async (q = "") => {
    try {
      setLoading(true);
      const url = q
        ? `${apiBase}/api/co-pays/search-list?page=${page}&size=${rowsPerPage}&q=${q}`
        : `${apiBase}/api/co-pays?page=${page}&size=${rowsPerPage}`;
      const res = await httpClient.get(url);
      setItems(res.data.content || []);
      setTotalElements(res.data.totalElements || 0);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCoPays(searchInput);
  }, [page, rowsPerPage]);

  const onEdit = async (rec) => {
    try {
      console.log("Setting row data:", rec);
      setLoading(true);
      // 1. Get the main Co-Pay record
      const res = await httpClient.get(`${apiBase}/api/co-pays/${rec.id}`);
      const fullRecord = res.data;

      // 2. HYDRATION: Trigger remote searches for related Autocomplete IDs
      // Check if the record has a policyItemId and trigger its specific search
      if (fullRecord.policyItemId) {
        loadPolicyItems(fullRecord.policyItemId);
      }

      // If you have arrays of IDs (like terminals), you might need a bulk fetch
      // or loop through them if your search API supports searching by ID
      if (fullRecord.terminalIds && fullRecord.terminalIds.length > 0) {
        // Assuming loadTerminals(id) works for fetching a specific record
        loadTerminals(fullRecord.terminalIds[0]);
      }

      if (fullRecord.policyTypeIds && fullRecord.policyTypeIds.length > 0) {
        loadPolicyTypes(fullRecord.policyTypeIds[0]);
      }

      // 3. Set record and open dialog
      setEditingRecord(fullRecord);
      setDialogOpen(true);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      // 1. Convert complex objects from dynamic lists back to simple ID arrays
      policyTypeIds: Array.isArray(data.policyTypeIds)
        ? data.policyTypeIds.map((item) => item.policyTypeId || item)
        : [],
      terminalIds: Array.isArray(data.terminalIds)
        ? data.terminalIds.map((item) => item.terminalId || item)
        : [],
      // 2. Ensure Numeric fields are actual Numbers, not Strings
      policyItemId: data.policyItemId ? Number(data.policyItemId) : null,
      fixedAmountByBeneficiary: data.fixedAmountByBeneficiary
        ? Number(data.fixedAmountByBeneficiary)
        : 0,
      // 3. Ensure @NotBlank fields are not empty strings
      benefit: data.benefit || "Default Benefit",
    };

    setSaving(true);
    try {
      const isEdit = data.id && data.id > 0;
      const url = isEdit ? `${apiBase}/api/co-pays/${data.id}` : `${apiBase}/api/co-pays`;
      await httpClient[isEdit ? "put" : "post"](url, payload);
      showSnackBar("Success", "success", `Co-Pay ${isEdit ? "updated" : "created"}`);
      setDialogOpen(false);
      loadCoPays(searchInput);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setSaving(false);
    }
  };

  const onDeleteConfirmed = async () => {
    try {
      if (Array.isArray(itemToDelete)) {
        await httpClient.post(`${apiBase}/api/co-pays/bulk-delete`, { ids: itemToDelete });
      } else {
        await httpClient.delete(`${apiBase}/api/co-pays/${itemToDelete.id}`);
      }
      showSnackBar("Success", "success", "Deleted successfully");
      loadCoPays(searchInput);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setConfirmationDialogOpen(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Card>
          <MDBox display="flex" justifyContent="space-between" p={3}>
            <MDButton
              variant="gradient"
              color="info"
              onClick={() => {
                setEditingRecord(coPayRecord);
                setDialogOpen(true);
              }}
            >
              Add Co-Pay <Icon>add</Icon>
            </MDButton>
            <MDBox display="flex" gap={1}>
              <MDInput
                size="small"
                label="Search..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyUp={(e) => e.key === "Enter" && loadCoPays(searchInput)}
              />
              <CustomMenuButton
                buttonTitle="Actions"
                items={[
                  {
                    key: "del",
                    text: "Delete Selected",
                    icon: "delete",
                    iconColor: "error",
                  },
                ]}
                onItemClicked={() => {
                  setItemToDelete(selectedIds);
                  setConfirmationDialogOpen(true);
                }}
              />
            </MDBox>
          </MDBox>
          <MDBox px={3} pb={3}>
            <CoPaysTableData
              data={items}
              selectedIds={selectedIds}
              onToggle={(id) =>
                setSelectedIds((prev) =>
                  prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                )
              }
              onEdit={onEdit}
              onDelete={(rec) => {
                setItemToDelete(rec);
                setConfirmationDialogOpen(true);
              }}
            />
            <CustomTablePagination
              page={page}
              rowsPerPage={rowsPerPage}
              totalItems={totalElements}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
            />
          </MDBox>
        </Card>
      </MDBox>

      <SchemaDialogForm
        open={dialogOpen}
        title="Co-Pay Setup"
        schema={coPayFormSchema}
        initialRecord={editingRecord}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onSubmit={onSubmit}
        deps={{
          policyTypes,
          terminals,
          policyItems,
          policyItemsLoading,
          onSearchPolicyItems: loadPolicyItems,
          policyTypesLoading,
          terminalsLoading,
          onSearchPolicyTypes: loadPolicyTypes,
          onSearchTerminals: loadTerminals,
        }}
      />

      <ConfirmationDialog
        open={confirmationDialogOpen}
        onCancel={() => setConfirmationDialogOpen(false)}
        onOk={onDeleteConfirmed}
        title="Confirm Delete"
        message="Are you sure you want to delete this Co-Pay?"
      />

      <MDSnackbar
        color={sb.type}
        title={sb.title}
        content={sb.message}
        open={sb.open}
        onClose={() => setSb({ ...sb, open: false })}
      />
    </DashboardLayout>
  );
}

export default CoPays;
